/**
 * AlignmentService.ts
 * Service pour l'alignement et la distribution des formes
 * Supporte l'alignement horizontal/vertical et la distribution équidistante
 */
import { logger } from '../utils/ProfessionalLogger';
import type { ShapeData } from '../types/ShapeTypes';
import { useAppStore } from './StateService';

export type AlignmentType = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom';
export type DistributionType = 'horizontal' | 'vertical' | 'both';

interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  centerX: number;
  centerY: number;
}

/**
 * Service d'alignement et de distribution
 */
export class AlignmentService {
  private static instance: AlignmentService | null = null;

  public static getInstance(): AlignmentService {
    if (!AlignmentService.instance) {
      AlignmentService.instance = new AlignmentService();
    }
    return AlignmentService.instance;
  }

  /**
   * Calcule les bounds d'un ensemble de formes
   */
  private calculateBounds(shapes: ShapeData[]): Bounds {
    if (shapes.length === 0) {
      return { minX: 0, minY: 0, maxX: 0, maxY: 0, centerX: 0, centerY: 0 };
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    shapes.forEach(shape => {
      const x = shape.x || 0;
      const y = shape.y || 0;
      const width = shape.width || 100;
      const height = shape.height || 100;

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + width);
      maxY = Math.max(maxY, y + height);
    });

    return {
      minX,
      minY,
      maxX,
      maxY,
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
    };
  }

  /**
   * Aligne les formes sélectionnées selon le type d'alignement
   */
  public alignShapes(shapeIds: string[], alignment: AlignmentType): void {
    const store = useAppStore.getState();
    const shapes = store.shapes.filter(s => shapeIds.includes(s.id));

    if (shapes.length < 2) {
      logger.warn('⚠️ Alignement: Au moins 2 formes sont nécessaires');
      return;
    }

    const bounds = this.calculateBounds(shapes);
    const updates: Array<{ id: string; updates: Partial<ShapeData> }> = [];

    shapes.forEach(shape => {
      const width = shape.width || 100;
      const height = shape.height || 100;
      let newX = shape.x || 0;
      let newY = shape.y || 0;

      switch (alignment) {
        case 'left':
          newX = bounds.minX;
          break;
        case 'center':
          newX = bounds.centerX - width / 2;
          break;
        case 'right':
          newX = bounds.maxX - width;
          break;
        case 'top':
          newY = bounds.minY;
          break;
        case 'middle':
          newY = bounds.centerY - height / 2;
          break;
        case 'bottom':
          newY = bounds.maxY - height;
          break;
      }

      if (newX !== (shape.x || 0) || newY !== (shape.y || 0)) {
        updates.push({
          id: shape.id,
          updates: { x: newX, y: newY },
        });
      }
    });

    if (updates.length > 0) {
      store.updateShapes(updates);
      // P3 : commit() injecté par historyMiddleware, pas typé dans AppActions.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (store as any).commit?.();
      logger.info(`✅ ${updates.length} forme(s) alignée(s) (${alignment})`);
    }
  }

  /**
   * Distribue les formes de manière équidistante
   */
  public distributeShapes(shapeIds: string[], direction: DistributionType): void {
    const store = useAppStore.getState();
    const shapes = store.shapes.filter(s => shapeIds.includes(s.id));

    if (shapes.length < 3) {
      logger.warn('⚠️ Distribution: Au moins 3 formes sont nécessaires');
      return;
    }

    // Trier les formes selon leur position
    const sortedShapes = [...shapes].sort((a, b) => {
      if (direction === 'horizontal' || direction === 'both') {
        return (a.x || 0) - (b.x || 0);
      } else {
        return (a.y || 0) - (b.y || 0);
      }
    });

    const bounds = this.calculateBounds(sortedShapes);
    const updates: Array<{ id: string; updates: Partial<ShapeData> }> = [];

    if (direction === 'horizontal' || direction === 'both') {
      // Calculer l'espace total disponible
      const totalWidth = sortedShapes.reduce((sum, shape) => sum + (shape.width || 100), 0);
      const availableSpace = bounds.maxX - bounds.minX - totalWidth;
      const gap = availableSpace / (sortedShapes.length - 1);

      let currentX = bounds.minX;
      sortedShapes.forEach(shape => {
        const width = shape.width || 100;
        if (
          shape.id !== sortedShapes[0].id &&
          shape.id !== sortedShapes[sortedShapes.length - 1].id
        ) {
          updates.push({
            id: shape.id,
            updates: { x: currentX },
          });
        }
        currentX += width + gap;
      });
    }

    if (direction === 'vertical' || direction === 'both') {
      // Calculer l'espace total disponible
      const totalHeight = sortedShapes.reduce((sum, shape) => sum + (shape.height || 100), 0);
      const availableSpace = bounds.maxY - bounds.minY - totalHeight;
      const gap = availableSpace / (sortedShapes.length - 1);

      let currentY = bounds.minY;
      sortedShapes.forEach(shape => {
        const height = shape.height || 100;
        if (
          shape.id !== sortedShapes[0].id &&
          shape.id !== sortedShapes[sortedShapes.length - 1].id
        ) {
          const existingUpdate = updates.find(u => u.id === shape.id);
          if (existingUpdate) {
            existingUpdate.updates.y = currentY;
          } else {
            updates.push({
              id: shape.id,
              updates: { y: currentY },
            });
          }
        }
        currentY += height + gap;
      });
    }

    if (updates.length > 0) {
      store.updateShapes(updates);
      // P3 : commit() injecté par historyMiddleware, pas typé dans AppActions.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (store as any).commit?.();
      logger.info(`✅ ${updates.length} forme(s) distribuée(s) (${direction})`);
    }
  }

  /**
   * Centre les formes sélectionnées par rapport au canvas
   */
  public centerShapes(shapeIds: string[]): void {
    const store = useAppStore.getState();
    const shapes = store.shapes.filter(s => shapeIds.includes(s.id));

    if (shapes.length === 0) return;

    const templateWidth = store.canvas.templateWidth;
    const templateHeight = store.canvas.templateHeight;
    const bounds = this.calculateBounds(shapes);

    const centerX = templateWidth / 2;
    const centerY = templateHeight / 2;
    const offsetX = centerX - bounds.centerX;
    const offsetY = centerY - bounds.centerY;

    const updates: Array<{ id: string; updates: Partial<ShapeData> }> = shapes.map(shape => ({
      id: shape.id,
      updates: {
        x: (shape.x || 0) + offsetX,
        y: (shape.y || 0) + offsetY,
      },
    }));

    store.updateShapes(updates);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (store as any).commit?.();
    logger.info(`✅ ${shapes.length} forme(s) centrée(s)`);
  }
}

export const alignmentService = AlignmentService.getInstance();
